import { z } from 'zod';

/**
 * Subscription bundles (spec §5). Names are provisional; limits marked
 * ASSUMPTION below were not specified in the original request and are recorded
 * in docs/decisions/ADR-0006-plan-matrix.md. Everything here is a *default* —
 * the platform console stores the effective limits per subscription so a
 * commercial decision does not require a code change.
 */
export const PlanCode = z.enum(['essential', 'solo', 'center']);
export type PlanCode = z.infer<typeof PlanCode>;

/** Feature flags an entitlement can carry. Enforced on the server AND in the signed offline lease. */
export const Feature = z.enum([
  'patients',
  'dental_chart',
  'clinical_notes',
  'imaging',
  'prescriptions',
  'appointments',
  'whatsapp_drafts',
  'patient_finance', // patient financial fields, invoices, balances
  'finance_department',
  'inventory_department',
  'laboratory_department',
  'multi_dentist', // >1 dentist, commissions and settlements
  'commissions',
  'public_page', // clinic landing page + public booking
  'desktop_offline',
  'imports',
  'exports',
  /**
   * Dermatology and aesthetic medicine: the face and body chart, zone
   * findings, procedures with a dose (units of toxin, ml of filler, laser
   * shots), product and lot number, and the derma/aesthetic procedure sets.
   * Decided 2026-10-05: in Center by default; the console can grant it to
   * any subscription.
   */
  'derma_aesthetics',
]);
export type Feature = z.infer<typeof Feature>;

/**
 * What a clinic practises. A clinic picks any combination (a dental and
 * dermatology centre is common); the interface shows the charts and the
 * procedure sets that match, and the wording follows ("dentists" becomes
 * "practitioners" where nobody treats teeth).
 */
export const Specialty = z.enum(['dental', 'dermatology', 'aesthetics']);
export type Specialty = z.infer<typeof Specialty>;

export const PlanLimits = z.object({
  maxDentists: z.number().int().positive().nullable(), // null = configured per subscription
  maxActiveUsers: z.number().int().positive().nullable(),
  maxDevicesPerUser: z.number().int().positive().nullable(),
  storageGb: z.number().positive().nullable(),
});
export type PlanLimits = z.infer<typeof PlanLimits>;

export const Entitlement = z.object({
  plan: PlanCode,
  features: z.array(Feature),
  limits: PlanLimits,
  /** Bumped whenever a clinic's entitlement changes; offline leases carry it so
   *  a stale device can be detected and its lease refused on next contact. */
  version: z.number().int().nonnegative(),
});
export type Entitlement = z.infer<typeof Entitlement>;

const BASE: Feature[] = [
  'patients',
  'dental_chart',
  'clinical_notes',
  'imaging',
  'prescriptions',
  'appointments',
  'whatsapp_drafts',
  'public_page',
  'desktop_offline',
  'imports',
  'exports',
];

export const PLAN_DEFAULTS: Record<PlanCode, Omit<Entitlement, 'version'>> = {
  essential: {
    plan: 'essential',
    features: [...BASE],
    limits: {
      // Decided 2026-09-28: Essential and Solo are one dentist with as many
      // users as the clinic needs; they differ in features, not in people.
      maxDentists: 1,
      maxActiveUsers: null,
      maxDevicesPerUser: 2,
      storageGb: 5,
    },
  },
  solo: {
    plan: 'solo',
    features: [...BASE, 'patient_finance', 'finance_department', 'inventory_department'],
    limits: {
      // Decided 2026-09-28: one dentist, any number of users. Center is the
      // plan with several dentists.
      maxDentists: 1,
      maxActiveUsers: null,
      maxDevicesPerUser: 3,
      storageGb: 25,
    },
  },
  center: {
    plan: 'center',
    features: [
      ...BASE,
      'patient_finance',
      'finance_department',
      'inventory_department',
      'laboratory_department',
      'multi_dentist',
      'commissions',
      'derma_aesthetics',
    ],
    limits: {
      // NOT "unlimited" — commercial limits are per-subscription and must be set
      // by the platform console before a real subscription is sold.
      maxDentists: null,
      maxActiveUsers: null,
      maxDevicesPerUser: 5,
      storageGb: null,
    },
  },
};

export function planEntitlement(plan: PlanCode, version = 1): Entitlement {
  return { ...PLAN_DEFAULTS[plan], features: [...PLAN_DEFAULTS[plan].features], version };
}

export function hasFeature(e: Pick<Entitlement, 'features'>, f: Feature): boolean {
  return e.features.includes(f);
}

/**
 * Fields stripped from every response, export, desktop cache and analytics
 * payload when the clinic's plan lacks `patient_finance` (spec §5). Hiding them
 * in the UI alone is not sufficient.
 */
export const FINANCIAL_FIELD_KEYS = [
  'price',
  'unitPrice',
  'amount',
  'amountMinor',
  'balance',
  'balanceMinor',
  'discount',
  'discountMinor',
  'tax',
  'taxMinor',
  'total',
  'totalMinor',
  'paid',
  'paidMinor',
  'outstanding',
  'outstandingMinor',
  'currency',
  'invoiceId',
  'invoiceNumber',
  'cost',
  'costMinor',
] as const;
